import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearch } from "../../../../contexts/SearchContext";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { useAppContext } from "../../../../contexts/AppContext";
import { useAgentCommissionSettings } from "../../../../features/agentEntitlements/useAgentCommissionSettings";
import { sumCheckedRawSupplementFares } from "../../adapters/flightOfferAdapter";
import {
  buildPricedSelfComposeCombinedOffer,
  computeSelfComposePricingSsot,
  overlayEngineClientOnSelfComposeSsot,
} from "../selfCompose/selfComposeSummaryUtils";
import {
  BOOKING_FLOW_STEP_ORDER,
  canAdvanceFromStep,
  canEnterStep,
  resolveNextStepIndex,
  resolvePrevStepIndex,
  resolveStepWhenPassengersSkipped,
} from "../wizard/flightWizardStepResolve";
import {
  EMPTY_PRICING,
  computeFallbackFlightPricing,
  computeFlightBookingPricing,
  flightPricingEqual,
} from "./flightBookingPricing";
import { BookingFlowContext } from "./bookingFlowReactContext";

export { BOOKING_FLOW_STEP_ORDER };

/** עתידי: טיסה + מלון */
export const BOOKING_FLOW_ADD_HOTEL_STEP = "addHotel";

/** כשאין SearchContext — ניווט ללא self-compose */
export const DEFAULT_FLIGHT_BOOKING_SEARCH_GATE = {
  rtCount: 0,
  outboundCount: 0,
  inboundCount: 0,
  needsSelfComposePickStep: false,
  partialSelfCompose: false,
  outboundOnlyNoReturn: false,
  parallelOk: false,
  allowSelfComposeChoice: false,
};
const DEBUG_ENDPOINT =
  "http://127.0.0.1:7626/ingest/ca460569-320b-4741-be20-10cc54aa7608";

function postDebugLog({ runId, hypothesisId, location, message, data }) {
  return;
}

function initialBookingFlowState() {
  return {
    currentStep: "search",
    flowType: null,
    selectedFlightOffer: null,
    selectedHotelOffer: null,
    passengers: [],
    passengersStepValid: false,
    extras: {
      baggage: [],
      supplementPaxSelections: {},
      supplementUnitFares: {},
      supplementUnitFaresIls: {},
    },
    pricing: { ...EMPTY_PRICING },
    draftStatus: "idle",
    paymentStatus: "idle",
    bookingStatus: "idle",
    mockBookingReference: null,
  };
}

function addHotelResolved(s) {
  if (s.flowType === "flight_only") return true;
  if (s.flowType === "flight_hotel") return s.selectedHotelOffer !== null;
  return false;
}

/** הסבר קצר ל-DEV כש־canAdvanceFromStep === false (חייב להתאים ללוגיקת canAdvanceFromStep). */
function advanceBlockedReason(step, s, gate) {
  switch (step) {
    case "search":
      return s.flowType != null
        ? "search_blocked_unknown"
        : "search_flowType_null";
    case "results": {
      if (gate.partialSelfCompose && !gate.outboundOnlyNoReturn) {
        return "results_partial_self_compose_incomplete_parallel";
      }
      if (gate.needsSelfComposePickStep) {
        return "results_needs_self_compose_unexpected_block";
      }
      return s.selectedFlightOffer == null
        ? "results_selectedFlightOffer_null"
        : "results_blocked_unknown";
    }
    case "selfComposePick":
      return !(
        s.selectedFlightOffer?.isSelfComposeCombined ||
        s.selectedFlightOffer?.source === "selfCompose"
      )
        ? "selfComposePick_requires_self_compose_offer"
        : "selfComposePick_blocked_unknown";
    case "flightDetails":
      return s.selectedFlightOffer == null
        ? "flightDetails_selectedFlightOffer_null"
        : "flightDetails_blocked_unknown";
    case "extras":
      return !(s.passengersStepValid && s.passengers.length > 0)
        ? "extras_passengers_not_valid_or_empty"
        : "extras_blocked_unknown";
    case "passengers":
      return !(s.passengersStepValid && s.passengers.length > 0)
        ? "passengers_not_valid_or_empty"
        : "passengers_blocked_unknown";
    case "review":
      return !(s.passengersStepValid && s.passengers.length > 0)
        ? "review_passengers_invalid"
        : s.selectedFlightOffer == null
          ? "review_selectedFlightOffer_null"
          : "review_blocked_unknown";
    case "draftPayment":
      return "draftPayment_terminal_step";
    case "success":
      return "success_terminal_step";
    default:
      return `unknown_step_${step}`;
  }
}

export function BookingFlowProvider({ children, searchGate = DEFAULT_FLIGHT_BOOKING_SEARCH_GATE }) {
  const [state, setState] = useState(() => initialBookingFlowState());
  const [flightReviewTermsAccepted, setFlightReviewTermsAccepted] = useState(false);

  useEffect(() => {
    if (state.currentStep !== "review") {
      setFlightReviewTermsAccepted(false);
    }
  }, [state.currentStep]);
  const {
    calculateLDSDealFareForRoomsComposition,
    roomInfo,
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
  } = useSearch();
  const {
    handleFeeData,
    roomsAndPaxInfo,
    ldsSupplement,
  } = useSelectedResult();
  const { getCreditCardFeePercent } = useAppContext();
  const { fixedFlightCommission } = useAgentCommissionSettings();

  const computePricingForState = useCallback(
    (snapshot, overrides = {}) => {
      const selectedFlight = overrides.selectedFlightOffer ?? snapshot.selectedFlightOffer;
      const nextExtras = overrides.extras ?? snapshot.extras;
      const nextPassengers = Array.isArray(overrides.passengers)
        ? overrides.passengers
        : snapshot.passengers;
      const nextHandleFeeData = overrides.handleFeeData ?? handleFeeData;
      const nextRoomsAndPaxInfo = overrides.roomsAndPaxInfo ?? roomsAndPaxInfo;
      const nextLdsSupplement = overrides.ldsSupplement ?? ldsSupplement;

      const outLegDeal =
        selectedFlight?.selfComposeLegs?.outboundRawDeal ??
        selectedSelfComposeOutboundDeal ??
        null;
      const inLegDeal =
        selectedFlight?.selfComposeLegs?.inboundRawDeal ??
        selectedSelfComposeInboundDeal ??
        null;
      const baggageRaw = sumCheckedRawSupplementFares(nextLdsSupplement);
      const paxLen =
        nextPassengers.length > 0 ? nextPassengers.length : 0;
      const rawSelfComposeSsot =
        outLegDeal || inLegDeal
          ? computeSelfComposePricingSsot(
              outLegDeal,
              inLegDeal,
              roomInfo,
              baggageRaw,
              paxLen
            )
          : null;
      const pricedSelfCompose =
        outLegDeal && inLegDeal
          ? buildPricedSelfComposeCombinedOffer(
              outLegDeal,
              inLegDeal,
              roomInfo,
              calculateLDSDealFareForRoomsComposition,
              { baggageRaw, passengersLength: paxLen }
            )
          : null;
      const selfComposeSsot = overlayEngineClientOnSelfComposeSsot(
        rawSelfComposeSsot,
        pricedSelfCompose
      );

      if (!selectedFlight) {
        if (selfComposeSsot) {
          return computeFallbackFlightPricing({
            selectedFlightOffer: null,
            selfComposeSsot,
            extras: nextExtras,
          });
        }
        return { ...EMPTY_PRICING };
      }

      return computeFlightBookingPricing({
        selectedFlightOffer: selectedFlight,
        selfComposeSsot,
        extras: nextExtras,
        calculateLDSDealFareForRoomsComposition,
        handleFeeData: nextHandleFeeData,
        roomsAndPaxInfo: nextRoomsAndPaxInfo,
        getCreditCardFeePercent,
        fixedFlightCommission,
      });
    },
    [
      handleFeeData,
      roomsAndPaxInfo,
      ldsSupplement,
      selectedSelfComposeOutboundDeal,
      selectedSelfComposeInboundDeal,
      roomInfo,
      calculateLDSDealFareForRoomsComposition,
      getCreditCardFeePercent,
      fixedFlightCommission,
    ]
  );

  const withComputedPricing = useCallback(
    (snapshot, overrides = {}) => ({
      ...snapshot,
      pricing: computePricingForState(snapshot, overrides),
    }),
    [computePricingForState]
  );

  useEffect(() => {
    setState((prev) => {
      const nextPricing = computePricingForState(prev);
      if (flightPricingEqual(prev.pricing, nextPricing)) return prev;
      return { ...prev, pricing: nextPricing };
    });
  }, [
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    roomInfo,
    ldsSupplement,
    handleFeeData,
    roomsAndPaxInfo,
    calculateLDSDealFareForRoomsComposition,
    getCreditCardFeePercent,
    fixedFlightCommission,
    computePricingForState,
  ]);

  const goToStep = useCallback(
    (step) => {
      setState((prev) => {
        const curIdx = BOOKING_FLOW_STEP_ORDER.indexOf(prev.currentStep);
        const resolved = resolveStepWhenPassengersSkipped(step);
        if (resolved === prev.currentStep) return prev;
        const targetIdx = BOOKING_FLOW_STEP_ORDER.indexOf(resolved);
        if (targetIdx <= curIdx) {
          if (resolved === "results") {
            // #region agent log
            postDebugLog({
              runId: "run1",
              hypothesisId: "H4",
              location: "BookingFlowContext.jsx:383",
              message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
              data: {
                sourceFile: "BookingFlowContext.jsx",
                action: "goToStep_back_or_jump",
                fromStep: prev.currentStep,
                targetStep: resolved,
                pathnameBefore:
                  typeof window !== "undefined" ? window.location.pathname : "",
                nextPath: null,
                reason: "targetIdx_lte_curIdx",
              },
            });
            // #endregion
          }
          console.log("[FlightBookingMockup] goToStep (back/jump)", {
            from: prev.currentStep,
            to: resolved,
          });
          return { ...prev, currentStep: resolved };
        }
        if (canEnterStep(resolved, prev, searchGate)) {
          if (resolved === "results") {
            // #region agent log
            postDebugLog({
              runId: "run1",
              hypothesisId: "H4",
              location: "BookingFlowContext.jsx:409",
              message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
              data: {
                sourceFile: "BookingFlowContext.jsx",
                action: "goToStep_forward",
                fromStep: prev.currentStep,
                targetStep: resolved,
                pathnameBefore:
                  typeof window !== "undefined" ? window.location.pathname : "",
                nextPath: null,
                reason: "canEnterStep_true",
              },
            });
            // #endregion
          }
          console.log("[FlightBookingMockup] goToStep (forward)", {
            from: prev.currentStep,
            to: resolved,
          });
          return { ...prev, currentStep: resolved };
        }
        return prev;
      });
    },
    [searchGate]
  );

  const nextStep = useCallback(() => {
    setState((prev) => {
      if (!canAdvanceFromStep(prev.currentStep, prev, searchGate)) return prev;
      const nextIdx = resolveNextStepIndex(prev.currentStep, searchGate, prev);
      if (nextIdx < 0) return prev;
      const next = BOOKING_FLOW_STEP_ORDER[nextIdx];
      if (!canEnterStep(next, prev, searchGate)) return prev;
      console.log("[FlightBookingMockup] nextStep", {
        from: prev.currentStep,
        to: next,
        passengersCount: prev.passengers?.length ?? 0,
        passengersStepValid: prev.passengersStepValid,
      });
      return { ...prev, currentStep: next };
    });
  }, [searchGate]);

  const clearSelectedFlightOffer = useCallback(() => {
    setState((prev) => withComputedPricing({ ...prev, selectedFlightOffer: null }));
  }, [withComputedPricing]);

  const prevStep = useCallback(() => {
    setState((prev) => {
      const prevIdx = resolvePrevStepIndex(prev.currentStep, prev, searchGate);
      if (prevIdx < 0) return prev;
      const prevStepId = BOOKING_FLOW_STEP_ORDER[prevIdx];
      console.log("[FlightBookingMockup] prevStep", {
        from: prev.currentStep,
        to: prevStepId,
      });
      return { ...prev, currentStep: prevStepId };
    });
  }, [searchGate]);

  const setFlowType = useCallback((type) => {
    setState((prev) => {
      let next = { ...prev, flowType: type };
      if (type === "flight_only") {
        next = { ...next, selectedHotelOffer: null };
      }
      return withComputedPricing(next);
    });
  }, [withComputedPricing]);

  const selectFlightOffer = useCallback((mockOffer) => {
    setState((prev) =>
      withComputedPricing({
        ...prev,
        selectedFlightOffer: mockOffer,
        extras: {
          baggage: [],
          supplementPaxSelections: {},
          supplementUnitFares: {},
          supplementUnitFaresIls: {},
        },
      })
    );
  }, [withComputedPricing]);

  /**
   * בחירת הצעה מהרשימה + קידום שלבים באטום אחד.
   * כשה-URL כבר …/flights/results אבל currentStep עדיין "search", קריאה ל-nextStep בלבד מקדמת רק ל-results
   * והמשתמש לא רואה מעבר — לכן מבצעים כאן את כל קידומי ה-nextStep הרצופים עד flightDetails (או עצירה ב-selfComposePick).
   */
  const selectFlightOfferAndAdvanceBooking = useCallback(
    (mockOffer) => {
      setState((prev) => {
        let cur = withComputedPricing({
          ...prev,
          flowType: prev.flowType || "flight_only",
          selectedFlightOffer: mockOffer,
          extras: {
            baggage: [],
            supplementPaxSelections: {},
            supplementUnitFares: {},
            supplementUnitFaresIls: {},
          },
        });
        const maxHops = 4;
        const dev =
          typeof import.meta !== "undefined" && import.meta.env?.DEV === true;
        /** @type {string | null} */
        let blockedReason = null;

        const gateAudit = {
          partialSelfCompose: searchGate.partialSelfCompose,
          outboundOnlyNoReturn: searchGate.outboundOnlyNoReturn,
          needsSelfComposePickStep: searchGate.needsSelfComposePickStep,
        };

        for (let hop = 0; hop < maxHops; hop++) {
          if (
            cur.currentStep === "flightDetails" ||
            cur.currentStep === "selfComposePick"
          ) {
            break;
          }
          const canAdv = canAdvanceFromStep(
            cur.currentStep,
            cur,
            searchGate
          );
          if (!canAdv) {
            blockedReason = advanceBlockedReason(
              cur.currentStep,
              cur,
              searchGate
            );
            if (dev) {
              console.log("[FLIGHT_ADVANCE_AUDIT]", {
                phase: "loop_blocked",
                hop,
                step: cur.currentStep,
                canAdvance: false,
                blockedReason,
                gate: gateAudit,
                selectedOfferId: cur.selectedFlightOffer?.id ?? null,
              });
            }
            break;
          }
          const nextIdx = resolveNextStepIndex(
            cur.currentStep,
            searchGate,
            cur
          );
          if (nextIdx < 0) {
            blockedReason = "resolveNextStepIndex_negative";
            if (dev) {
              console.log("[FLIGHT_ADVANCE_AUDIT]", {
                phase: "no_next_index",
                hop,
                step: cur.currentStep,
                blockedReason,
                gate: gateAudit,
              });
            }
            break;
          }
          const nextStepId = BOOKING_FLOW_STEP_ORDER[nextIdx];
          if (!canEnterStep(nextStepId, cur, searchGate)) {
            blockedReason = `cannot_enter_${nextStepId}`;
            if (dev) {
              console.log("[FLIGHT_ADVANCE_AUDIT]", {
                phase: "canEnterStep_false",
                hop,
                fromStep: cur.currentStep,
                targetStep: nextStepId,
                blockedReason,
                gate: gateAudit,
                selectedOfferId: cur.selectedFlightOffer?.id ?? null,
                flowType: cur.flowType ?? null,
              });
            }
            break;
          }
          cur = { ...cur, currentStep: nextStepId };
        }

        const stepAdvanced = cur.currentStep !== prev.currentStep;

        if (dev) {
          console.log("[FLIGHT_ADVANCE_AUDIT]", {
            phase: "summary",
            from: prev.currentStep,
            to: cur.currentStep,
            canAdvance: stepAdvanced,
            blockedReason: stepAdvanced
              ? null
              : blockedReason ?? "no_step_change_after_select",
            gate: gateAudit,
            selectedOfferId: cur.selectedFlightOffer?.id ?? null,
          });
        }

        console.log("[FlightBookingMockup] selectFlightOfferAndAdvanceBooking", {
          from: prev.currentStep,
          to: cur.currentStep,
          offerId: mockOffer?.id ?? null,
        });
        return cur;
      });
    },
    [searchGate, withComputedPricing]
  );

  /**
   * שמירת הצעה + קידום שלב אחד באותו setState.
   * nextStep() לבד נכשל אחרי selectFlightOffer כי canAdvance עדיין רואה את ה-state הישן.
   */
  const selectFlightOfferAndNextStep = useCallback(
    (mockOffer) => {
      setState((prev) => {
        const selected = withComputedPricing({
          ...prev,
          selectedFlightOffer: mockOffer,
          extras: {
            baggage: [],
            supplementPaxSelections: {},
            supplementUnitFares: {},
            supplementUnitFaresIls: {},
          },
        });
        if (!canAdvanceFromStep(selected.currentStep, selected, searchGate)) {
          return selected;
        }
        const nextIdx = resolveNextStepIndex(
          selected.currentStep,
          searchGate,
          selected
        );
        if (nextIdx < 0) return selected;
        const next = BOOKING_FLOW_STEP_ORDER[nextIdx];
        if (!canEnterStep(next, selected, searchGate)) return selected;
        console.log("[FlightBookingMockup] selectFlightOfferAndNextStep", {
          from: prev.currentStep,
          to: next,
          offerId: mockOffer?.id ?? null,
        });
        return { ...selected, currentStep: next };
      });
    },
    [searchGate, withComputedPricing]
  );

  const selectHotelOffer = useCallback((mockHotel) => {
    setState((prev) => withComputedPricing({ ...prev, selectedHotelOffer: mockHotel }));
  }, [withComputedPricing]);

  /** עדכון ישיר (למשל recovery/SSR) תוך שמירה על חסימת setState זהה. */
  const setPricing = useCallback((nextPricing) => {
    if (!nextPricing || typeof nextPricing !== "object") return;
    setState((prev) => {
      if (flightPricingEqual(prev.pricing, nextPricing)) return prev;
      return { ...prev, pricing: { ...nextPricing } };
    });
  }, []);

  /**
   * עדכון סינכרוני למחיר עבור קלטים חיצוניים (כמו handleFeeData) בלי useEffect.
   * ניתן להעביר override כדי לחשב מיד עם הערך החדש, לפני שספק context אחר מרנדר.
   */
  const recomputePricingNow = useCallback(
    (overrides = {}) => {
      setState((prev) => {
        const nextPricing = computePricingForState(prev, overrides);
        if (flightPricingEqual(prev.pricing, nextPricing)) return prev;
        return { ...prev, pricing: nextPricing };
      });
    },
    [computePricingForState]
  );

  const updatePassengers = useCallback((data) => {
    console.log("[FlightBookingMockup] updatePassengers", {
      count: Array.isArray(data) ? data.length : 0,
    });
    setState((prev) => withComputedPricing({ ...prev, passengers: data }));
  }, [withComputedPricing]);

  const setPassengersStepValid = useCallback((valid) => {
    setState((prev) => ({ ...prev, passengersStepValid: !!valid }));
  }, []);

  const updateExtras = useCallback((data) => {
    setState((prev) => {
      const nextExtras =
        typeof data === "function" ? data(prev.extras) : data;
      return withComputedPricing({ ...prev, extras: nextExtras }, { extras: nextExtras });
    });
  }, [withComputedPricing]);

  const saveDraftMock = useCallback(() => {
    setState((prev) => ({ ...prev, draftStatus: "saved" }));
  }, []);

  const startPaymentMock = useCallback(() => {
    setState((prev) => ({ ...prev, paymentStatus: "started" }));
  }, []);

  const confirmBookingMock = useCallback(() => {
    setState((prev) => ({
      ...prev,
      bookingStatus: "confirmed",
      paymentStatus: "completed",
      currentStep: "success",
      mockBookingReference: {
        orderNumber: `HR-${String(Date.now()).slice(-10)}`,
        pnr: "MOCK-7K2P",
        status: "אושר (מוקאפ)",
      },
    }));
  }, []);

  const resetFlow = useCallback(() => {
    setFlightReviewTermsAccepted(false);
    setState(() => initialBookingFlowState());
  }, []);

  const openResultsAfterPlanesSearch = useCallback(() => {
    const stackTop =
      typeof Error !== "undefined" && typeof Error.captureStackTrace !== "undefined"
        ? (() => {
            const e = {};
            Error.captureStackTrace(e, openResultsAfterPlanesSearch);
            return String(e.stack || "")
              .split("\n")
              .slice(0, 5)
              .join("\n");
          })()
        : String(new Error().stack || "")
            .split("\n")
            .slice(0, 5)
            .join("\n");
    setState((prev) =>
      withComputedPricing({
        ...prev,
        flowType: prev.flowType || "flight_only",
        currentStep: "results",
        selectedFlightOffer: null,
      })
    );
    // #region agent log
    postDebugLog({
      runId: "run1",
      hypothesisId: "H3",
      location: "BookingFlowContext.jsx:772",
      message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
      data: {
        sourceFile: "BookingFlowContext.jsx",
        action: "openResultsAfterPlanesSearch",
        fromStep: "unknown_async_setState",
        targetStep: "results",
        pathnameBefore:
          typeof window !== "undefined" ? window.location.pathname : "",
        nextPath: null,
        reason: "bridge_or_pathsync_called",
        stackTop,
      },
    });
    // #endregion
  }, [withComputedPricing]);

  const canAdvanceFromCurrentStep = useCallback(() => {
    return canAdvanceFromStep(state.currentStep, state, searchGate);
  }, [state, searchGate]);

  const isStepReachable = useCallback(
    (step) => {
      return canEnterStep(step, state, searchGate);
    },
    [state, searchGate]
  );

  const value = useMemo(
    () => ({
      ...state,
      searchGate,
      goToStep,
      nextStep,
      prevStep,
      setFlowType,
      selectFlightOffer,
      selectFlightOfferAndAdvanceBooking,
      selectFlightOfferAndNextStep,
      clearSelectedFlightOffer,
      selectHotelOffer,
      updatePassengers,
      setPassengersStepValid,
      updateExtras,
      setPricing,
      recomputePricingNow,
      saveDraftMock,
      startPaymentMock,
      confirmBookingMock,
      resetFlow,
      openResultsAfterPlanesSearch,
      canAdvanceFromCurrentStep,
      isStepReachable,
      flightReviewTermsAccepted,
      setFlightReviewTermsAccepted,
    }),
    [
      state,
      searchGate,
      goToStep,
      nextStep,
      prevStep,
      setFlowType,
      selectFlightOffer,
      selectFlightOfferAndAdvanceBooking,
      selectFlightOfferAndNextStep,
      clearSelectedFlightOffer,
      selectHotelOffer,
      updatePassengers,
      setPassengersStepValid,
      updateExtras,
      setPricing,
      recomputePricingNow,
      saveDraftMock,
      startPaymentMock,
      confirmBookingMock,
      resetFlow,
      openResultsAfterPlanesSearch,
      canAdvanceFromCurrentStep,
      isStepReachable,
      flightReviewTermsAccepted,
      setFlightReviewTermsAccepted,
    ]
  );

  return (
    <BookingFlowContext.Provider value={value}>
      {children}
    </BookingFlowContext.Provider>
  );
}
